import * as Sentry from '@sentry/vue'

import { OVERRIDE_NAMESPACES } from './constants'
import { getFallbackLocalTranslations, getLocalTranslations } from './translations'

/**
 * @param {Object} deps
 * @param {boolean} deps.isDevMode
 * @param {string|undefined} deps.locizeProjectId
 * @param {Set<string>} deps.allNamespaces
 * @param {{ buildI18nSentryContext: Function, reportI18nIssue: Function }} deps.sentryReporter
 */
export function createLocizeFetcher({ isDevMode, locizeProjectId, allNamespaces, sentryReporter }) {
  const { buildI18nSentryContext, reportI18nIssue } = sentryReporter
  const lastFetchDiagnostics = new Map()

  /**
   * @param {string} lang
   * @param {string} namespace
   */
  async function fetchLocizeNamespace(lang, namespace) {
    const locizeVersion = import.meta.env.VITE_LOCIZE_VERSION || 'production'
    const hasLocalFallback = Boolean(getLocalTranslations(lang, namespace))

    if (!locizeProjectId) {
      const localTranslations = getLocalTranslations(lang, namespace)
      const usedLocalFallback = Boolean(localTranslations)

      return {
        data: localTranslations || {},
        status: 'error',
        keysCount: usedLocalFallback ? Object.keys(localTranslations).length : 0,
        locizeUrl: null,
        hasLocalFallback,
        usedLocalFallback,
        error: 'Missing VITE_LOCIZE_PROJECT_ID',
      }
    }

    const locizeUrl = `https://api.locize.app/${locizeProjectId}/${locizeVersion}/${lang}/${namespace}`

    try {
      const response = await fetch(locizeUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch namespace ${namespace} for ${lang}: ${response.status}`)
      }
      const data = await response.json()
      const translations = data[namespace] || data
      const keysCount = Object.keys(translations).length

      if (keysCount === 0) {
        return {
          data: {},
          status: 'empty',
          keysCount: 0,
          locizeUrl,
          hasLocalFallback,
        }
      }

      return {
        data: translations,
        status: 'ok',
        keysCount,
        locizeUrl,
        hasLocalFallback,
      }
    } catch (error) {
      const localTranslations = getLocalTranslations(lang, namespace)
      const usedLocalFallback = Boolean(localTranslations)

      Sentry.captureException(error, {
        tags: { component: 'i18n', locale: lang, action: 'fetchLocizeNamespace' },
        extra: {
          namespace,
          lang,
          locizeUrl,
          hasLocalFallback,
          usedLocalFallback,
          action: 'fetchLocizeNamespace',
        },
      })

      return {
        data: localTranslations || {},
        status: 'error',
        keysCount: usedLocalFallback ? Object.keys(localTranslations).length : 0,
        locizeUrl,
        hasLocalFallback,
        usedLocalFallback,
        error: error.message,
      }
    }
  }

  /**
   * @param {string} lang
   * @returns {Promise<Object>}
   */
  async function fetchLocizeTranslations(lang) {
    const namespaceResults = {}

    try {
      const namespaces = [...allNamespaces].filter((ns) => !OVERRIDE_NAMESPACES.includes(ns))

      const namespacePromises = namespaces.map((namespace) =>
        fetchLocizeNamespace(lang, namespace).then((result) => ({ namespace, result }))
      )

      const overridePromises = OVERRIDE_NAMESPACES.map((namespace) =>
        fetchLocizeNamespace(lang, namespace).then((result) => ({ namespace, result }))
      )

      const results = await Promise.all([...namespacePromises, ...overridePromises])
      const mergedTranslations = {}
      let hasValidTranslations = false

      results.forEach(({ namespace, result }) => {
        const { data, status, keysCount, locizeUrl, hasLocalFallback, usedLocalFallback, error } =
          result
        let resolvedStatus = status
        let usedLocalFallbackForEmpty = false

        namespaceResults[namespace] = {
          status,
          keysCount,
          locizeUrl,
          hasLocalFallback,
          usedLocalFallback: Boolean(usedLocalFallback),
          ...(error ? { error } : {}),
        }

        if (Object.keys(data).length > 0) {
          mergedTranslations[namespace] = data
          hasValidTranslations = true
          return
        }

        const localTranslations = getLocalTranslations(lang, namespace)
        if (localTranslations) {
          mergedTranslations[namespace] = localTranslations
          hasValidTranslations = true
          resolvedStatus = 'empty_with_local_fallback'
          usedLocalFallbackForEmpty = true
        }

        namespaceResults[namespace] = {
          ...namespaceResults[namespace],
          status: resolvedStatus,
          usedLocalFallback: usedLocalFallbackForEmpty || Boolean(usedLocalFallback),
        }
      })

      lastFetchDiagnostics.set(lang, namespaceResults)

      if (!hasValidTranslations && !isDevMode) {
        const localFallback = getFallbackLocalTranslations(lang)
        reportI18nIssue(
          'warning',
          `Falling back to local translations for ${lang} due to empty or failed remote data`,
          {
            lang,
            action: 'fetchLocizeTranslations',
            namespaceResults,
            localFallbackNamespaceCount: Object.keys(localFallback).length,
          }
        )
        return localFallback
      }

      return mergedTranslations
    } catch (error) {
      const { tags, extra } = buildI18nSentryContext(lang, {
        action: 'fetchLocizeTranslations',
        namespaceResults: Object.keys(namespaceResults).length > 0 ? namespaceResults : null,
      })
      Sentry.captureException(error, {
        tags,
        extra: { ...extra, errorMessage: error.message },
      })
      return getFallbackLocalTranslations(lang)
    }
  }

  return { fetchLocizeNamespace, fetchLocizeTranslations, lastFetchDiagnostics }
}
